import { useEffect, useState } from "react";
import { BellRing, Hotel, Radar, Inbox } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { enablePush } from "@/lib/push";

const timeAgo = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const BrowserAlerts = () => {
  const [perm, setPerm] = useState(window.Notification?.permission || "unsupported");
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    setBusy(true);
    try {
      const wasGranted = perm === "granted";
      await enablePush();
      setPerm("granted");
      if (wasGranted) await api.post("/push/test");
      toast.success(wasGranted ? "Notifica di prova inviata" : "Avvisi push attivi su questo telefono");
    } catch (e) {
      setPerm(window.Notification?.permission || "unsupported");
      toast.error(e.message || "Attivazione non riuscita");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" data-testid="notifications-enable-browser-button" onClick={ask} disabled={busy} className="ff-btn-secondary w-full text-sm mt-4 border-[#10B981]">
      <BellRing size={18} /> {perm === "granted" ? "Avvisi push attivi • invia prova" : "Attiva avvisi push sul telefono (anche ad app chiusa)"}
    </button>
  );
};

export const NotificationsSheet = ({ open, onClose, items, profile, onOpenRide, onMarkRead, onProfile }) => {
  useEffect(() => {
    if (open) onMarkRead();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const cities = profile?.home_cities || [];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="notifications-sheet" className="ff-glass-panel w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="ff-display text-2xl font-extrabold text-white">Smart Match</SheetTitle>
          <SheetDescription className="text-[#94A3B8]">{t("Avvisi automatici quando arriva una corsa sulla tua zona o rotta abituale.")}</SheetDescription>
        </SheetHeader>
        <div className="mt-5 ff-card p-4 flex flex-col gap-2">
          <span className="ff-label !mb-0">{t("Le tue zone")}</span>
          {cities.length ? (
            <div className="flex flex-wrap gap-2" data-testid="notifications-cities">{cities.map((c) => <span key={c} className="ff-badge ff-badge-green">{c}</span>)}</div>
          ) : (
            <p className="text-sm text-[#94A3B8]">{t("Nessuna zona impostata.")}</p>
          )}
          <button type="button" data-testid="notifications-edit-cities-button" onClick={onProfile} className="text-left text-sm font-semibold text-[#93C5FD] hover:text-white mt-1">{t("Modifica zone nel Profilo")}</button>
        </div>
        <BrowserAlerts />
        <div className="mt-5 flex flex-col gap-2" data-testid="notifications-list">
          {items.length === 0 && <div className="flex flex-col items-center gap-2 py-10 text-[#94A3B8] text-sm"><Inbox size={24} />{t("Nessun avviso per ora.")}</div>}
          {items.map((n) => (
            <button key={n.id} type="button" data-testid={`notification-item-${n.id}`} onClick={() => n.share_code && onOpenRide(n.share_code)} className="ff-card ff-card-hover p-4 text-left flex gap-3">
              {n.kind === "concierge" ? <Hotel size={20} className="text-[#3B82F6] shrink-0 mt-0.5" /> : <Radar size={20} className="text-[#10B981] shrink-0 mt-0.5" />}
              <span className="min-w-0">
                <span className="block font-bold text-white">{n.title}</span>
                <span className="block text-sm text-[#94A3B8]">{n.body}</span>
                <span className="block text-xs text-[#52525b] mt-1">{timeAgo(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
